import { Utilities } from "../utilities/utilities.js";
import { WindowMetrics } from "../utilities/window-metrics.js";

var
    wipeMode = {
        fixedDuration: 0,
        fixedSpeed: 1
    },

    settings = {
        animation: {
            wipe: {
                ease: Power1.easeOut,
                slowMoScale: 5
            }
        }
    };

class Tab {
    constructor(button, content, buttonClickCallback) {
        var t = this;

        t.button = button;

        t.innerContent = content;
        t.content = document.createElement("div");
        t.innerContent.parentNode.insertBefore(t.content, t.innerContent);
        t.content.appendChild(t.innerContent);

        // Setup tab button
        t.button.addEventListener("click", buttonClickCallback);
        
        // Set visibility
        t.content.style.display = "none";
    }

    onNavigateToTab(immediate) {
        // ...
    }

    onNavigateFromTab(immediate) {
        // ...
    }
}

export class TabContainer {
    constructor(containerElement, wipeMode, mobileWipeSpeed, desktopWipeSpeed) {
        var t = this;

        t.wipeMode = wipeMode;
        t.mobileWipeSpeed = mobileWipeSpeed;
        t.desktopWipeSpeed = desktopWipeSpeed;
        t.navigationTimeline = null;
        t.state = {
            currentTabIndex: null
        };

        // Get references to DOM elements
        t.container = containerElement;
        t.tabPanelContent = Utilities.getChildElement(t.container, "div");
        t.tabButtonContainer = t.tabPanelContent.querySelector(".tab-buttons");
        t.tabContentContainer = t.tabPanelContent.querySelector(".tab-content");

        // Setup tabs
        t.tabs = [];
        var
            tabButtonElements = Utilities.getChildElements(t.tabButtonContainer, "div"),
            tabContentElements = Utilities.getChildElements(t.tabContentContainer, "div");

        for (let i = 0; i < tabButtonElements.length; i++) {
            var tab = new Tab(
                tabButtonElements[i],
                tabContentElements[i],
                function (event) {
                    t.navigate(i, false, event);
                });

            t.tabs.push(tab);
        }

        // Set initial state
        t.navigate(0, true);

        t.container.style.visibility = "visible";
    }

    static get wipeMode() {
        return wipeMode;
    }

    navigate(toTabIndex, immediate, event) {
        var t = this;

        // Don't do anything if we're on the requested tab
        if (toTabIndex === t.state.currentTabIndex)
            return;

        // Stop any ongoing animation
        if (t.navigationTimeline)
            t.navigationTimeline.progress(1);

        if (immediate || t.state.currentTabIndex === null) {
            // Hide current tab
            if (t.state.currentTabIndex !== null) {
                var fromTab = t.tabs[t.state.currentTabIndex];

                fromTab.button.classList.remove("selected");

                Utilities.resetStyles(fromTab.content);
                Utilities.resetStyles(fromTab.innerContent);
                fromTab.content.style.display = "none";

                // Send post-navigation event
                fromTab.onNavigateFromTab(immediate);
            }

            // Show new tab
            var toTab = t.tabs[toTabIndex];

            toTab.button.classList.add("selected");
            Utilities.resetStyles(toTab.content);
            Utilities.resetStyles(toTab.innerContent);

            // Send pre-navigation event
            toTab.onNavigateToTab(immediate);
        } else {
            let
                fromTab = t.tabs[t.state.currentTabIndex],
                toTab = t.tabs[toTabIndex],
                wipeDuration;

            // Set initial state
            Utilities.resetStyles(fromTab.content);
            Utilities.resetStyles(fromTab.innerContent);
            Utilities.resetStyles(toTab.content);
            Utilities.resetStyles(toTab.innerContent);

            t.tabContentContainer.style.width = Utilities.numberToPx(t.tabPanelContent.clientWidth);
            t.tabContentContainer.style.height = Utilities.numberToPx(fromTab.content.clientHeight);

            fromTab.content.style.position = "absolute";
            fromTab.content.style.zIndex = "1";
            fromTab.content.style.boxShadow = "rgba(255,255,255,0.75) 0px -50px 100px";
            fromTab.content.style.borderTop = "solid 1px #e6e6e6";

            toTab.content.style.position = "absolute";

            // Update buttons
            fromTab.button.classList.remove("selected");
            toTab.button.classList.add("selected");

            // Send pre-navigation event
            toTab.onNavigateToTab(immediate);

            // Determine wipe duration based on the selected mode
            switch (t.wipeMode) {
                case wipeMode.fixedSpeed:
                    if (WindowMetrics.isMobile())
                        wipeDuration = toTab.content.clientHeight / t.mobileWipeSpeed;
                    else
                        wipeDuration = toTab.content.clientHeight / t.desktopWipeSpeed;
                    break;

                default: // wipeMode.fixedDuration
                    if (WindowMetrics.isMobile())
                        wipeDuration = t.mobileWipeSpeed;
                    else
                        wipeDuration = t.desktopWipeSpeed;
                    break;
            };

            if (event.shiftKey === true)
                wipeDuration *= settings.animation.wipe.slowMoScale;

            // Animate transition
            t.navigationTimeline = new TimelineMax();

            t.navigationTimeline.fromTo(
                t.tabContentContainer,
                wipeDuration,
                {
                    height: fromTab.content.clientHeight + 2
                },
                {
                    ease: settings.animation.wipe.ease,
                    height: toTab.content.clientHeight + 2 // Not sure where the extra 2 pixels are coming from; probably top & bottom borders
                },
                0);

            t.navigationTimeline.fromTo(
                fromTab.content,
                wipeDuration,
                {
                    // This is due to the 1px top border we added above
                    top: -1,
                    height: fromTab.content.clientHeight
                },
                {
                    ease: settings.animation.wipe.ease,
                    top: toTab.content.clientHeight - 1,
                    height: 0
                },
                0);

            t.navigationTimeline.fromTo(
                fromTab.innerContent,
                wipeDuration,
                {
                    top: 0
                },
                {
                    ease: settings.animation.wipe.ease,
                    top: -toTab.content.clientHeight
                },
                0);

            t.navigationTimeline.fromTo(
                toTab.content,
                wipeDuration,
                {
                    height: 0
                },
                {
                    ease: settings.animation.wipe.ease,
                    height: toTab.content.clientHeight
                },
                0);

            var progressObject = {};
            t.navigationTimeline.fromTo(
                progressObject,
                wipeDuration / 3,
                {
                    shadowOpacity: 0.75
                },
                {
                    ease: Power0.easeNone,
                    shadowOpacity: 0,
                    onUpdate: function () {
                        fromTab.content.style.boxShadow = `rgba(228,228,228,${progressObject.shadowOpacity}) 0px -50px 100px`;
                    }
                },
                wipeDuration - (wipeDuration / 3));

            t.navigationTimeline.add(function () {
                t.navigationTimeline = null;

                Utilities.resetStyles(fromTab.content);
                Utilities.resetStyles(fromTab.innerContent);
                fromTab.content.style.display = "none";

                Utilities.resetStyles(toTab.content);
                Utilities.resetStyles(toTab.innerContent);

                Utilities.resetStyles(t.tabContentContainer);

                // Send post-navigation event
                fromTab.onNavigateFromTab(immediate);
            });
        }

        // Update state
        t.state.currentTabIndex = toTabIndex;
    }
}
